import React, { useState } from 'react'
import Dropzone from 'react-dropzone'
import {
    Box,
    Typography,
    Button,
    LinearProgress,
    Chip,
    Paper,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
    CloudUpload as UploadIcon,
    CheckCircle as CheckIcon,
    InsertDriveFile as FileIcon,
    Error as ErrorIcon,
    HourglassEmpty as PendingIcon,
    Sync as ProcessingIcon,
} from '@material-ui/icons'

import { useDocuments } from '../../hooks/useDocuments'
import { UploadProgress, DocumentUploadRecord, UploadStatus } from '../../types'

interface DocumentUploadZoneProps {
    auditId: number
    onUploadComplete?: () => void
    showAdditiveUploadNote?: boolean
}

interface UploadProgressDisplayProps {
    progress: UploadProgress
}

const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const formatSpeed = (bytesPerSecond: number): string => {
    return formatBytes(bytesPerSecond) + '/s'
}

const formatTime = (seconds: number): string => {
    if (seconds < 60) {
        return `${Math.round(seconds)}s`
    }
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.round(seconds % 60)
    return `${minutes}m ${remainingSeconds}s`
}

const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60)
        return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
    if (diffHours < 24)
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

const getStatusColor = (status: UploadStatus): string => {
    switch (status) {
        case 'COMPLETED':
            return '#10b981'
        case 'PROCESSING':
        case 'UPLOADED':
            return '#3b82f6'
        case 'PENDING':
            return '#f59e0b'
        case 'FAILED':
            return '#ef4444'
        default:
            return '#6b7280'
    }
}

const getStatusIcon = (status: UploadStatus) => {
    switch (status) {
        case 'COMPLETED':
            return CheckIcon
        case 'PROCESSING':
        case 'UPLOADED':
            return ProcessingIcon
        case 'PENDING':
            return PendingIcon
        case 'FAILED':
            return ErrorIcon
        default:
            return FileIcon
    }
}

const getStatusLabel = (status: UploadStatus): string => {
    switch (status) {
        case 'COMPLETED':
            return 'Completed'
        case 'PROCESSING':
            return 'Processing'
        case 'UPLOADED':
            return 'Uploaded'
        case 'PENDING':
            return 'Pending'
        case 'FAILED':
            return 'Failed'
        default:
            return status
    }
}

interface UploadHistoryCardProps {
    record: DocumentUploadRecord
}

const UploadHistoryCard: React.FC<UploadHistoryCardProps> = ({ record }) => {
    const StatusIcon = getStatusIcon(record.status)
    const statusColor = getStatusColor(record.status)

    return (
        <Paper
            elevation={1}
            style={{
                padding: 16,
                marginBottom: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
            }}
        >
            <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
            >
                <Box display="flex" alignItems="center" flex={1}>
                    <FileIcon
                        style={{
                            color: '#6b7280',
                            marginRight: 12,
                            fontSize: 32,
                        }}
                    />
                    <Box flex={1}>
                        <Typography
                            variant="body1"
                            style={{ fontWeight: 500, marginBottom: 4 }}
                        >
                            {record.original_filename}
                        </Typography>
                        <Box display="flex" alignItems="center">
                            <Typography variant="caption" color="textSecondary">
                                {formatBytes(record.file_size)}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ margin: '0 8px' }}
                            >
                                •
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {formatDate(record.created_date)}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ margin: '0 8px' }}
                            >
                                •
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                by {record.uploaded_by.name}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Chip
                    icon={<StatusIcon style={{ fontSize: 16 }} />}
                    label={getStatusLabel(record.status)}
                    size="small"
                    style={{
                        backgroundColor: statusColor + '15',
                        color: statusColor,
                        fontWeight: 500,
                        marginLeft: 12,
                    }}
                />
            </Box>
        </Paper>
    )
}

interface UploadHistorySectionProps {
    records: DocumentUploadRecord[]
}

const UploadHistorySection: React.FC<UploadHistorySectionProps> = ({
    records,
}) => {
    if (records.length === 0) {
        return null
    }

    // Show only the 2 most recent uploads
    const recentRecords = records.slice(0, 2)

    return (
        <Box style={{ marginBottom: 24 }}>
            <Typography
                variant="subtitle2"
                style={{ marginBottom: 12, fontWeight: 600 }}
            >
                Recent Uploads
            </Typography>
            {recentRecords.map((record) => (
                <UploadHistoryCard key={record.id} record={record} />
            ))}
        </Box>
    )
}

const UploadProgressDisplay: React.FC<UploadProgressDisplayProps> = ({
    progress,
}) => {
    return (
        <Box style={{ width: '100%', marginTop: 16 }}>
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                }}
            >
                <Typography variant="body2" color="textSecondary">
                    Uploading... {progress.percentage}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {formatBytes(progress.bytesUploaded)} /{' '}
                    {formatBytes(progress.totalBytes)}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={progress.percentage}
                style={{ height: 8, borderRadius: 4 }}
            />
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 8,
                }}
            >
                <Typography variant="caption" color="textSecondary">
                    Speed: {formatSpeed(progress.speed)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    ETA: {formatTime(progress.eta)}
                </Typography>
            </Box>
        </Box>
    )
}

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: theme.spacing(2),
    },
    uploadSection: {
        width: '100%',
        maxWidth: 800,
        padding: theme.spacing(2),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.spacing(1),
    },
    dropzone: {
        border: `2px dashed ${theme.palette.grey[300]}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(3),
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light + '10',
        },
    },
    dropzoneActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light + '20',
    },
}))

export const DocumentUploadZone: React.FC<DocumentUploadZoneProps> = ({
    auditId,
    onUploadComplete,
    showAdditiveUploadNote = false,
}) => {
    const classes = useStyles()
    const [lastFailedFile, setLastFailedFile] = useState<File | null>(null)
    const [lastUploadedFileName, setLastUploadedFileName] = useState<
        string | null
    >(null)

    const {
        isUploading,
        uploadProgress,
        uploadError,
        uploadDocuments,
        isProcessingExtraction,
        uploadHistory,
        isLoadingUploadHistory,
    } = useDocuments({
        auditId,
        paired: false,
    })

    const hasUploadedFile =
        uploadHistory?.some((record) => record.status === 'UPLOADED') ?? false

    const handleFileDrop = async (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0]
            try {
                await uploadDocuments(file)
                setLastFailedFile(null)
                setLastUploadedFileName(file.name)
                onUploadComplete?.()
            } catch (error) {
                setLastFailedFile(file)
                setLastUploadedFileName(null)
            }
        }
    }

    const handleRetryUpload = async () => {
        if (lastFailedFile) {
            try {
                await uploadDocuments(lastFailedFile)
                setLastFailedFile(null)
                setLastUploadedFileName(lastFailedFile.name)
                onUploadComplete?.()
            } catch (error) {
                // Error is already set in the hook
                setLastUploadedFileName(null)
            }
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.uploadSection}>
                <Typography variant="h6" gutterBottom align="center">
                    {lastUploadedFileName && !uploadError
                        ? `Successfully Uploaded: ${lastUploadedFileName}`
                        : 'Upload Lease Documents'}
                </Typography>
                {lastUploadedFileName && !uploadError ? (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        style={{ marginBottom: 16 }}
                    >
                        <CheckIcon
                            style={{
                                color: '#10b981',
                                marginRight: 8,
                                fontSize: 20,
                            }}
                        />
                        <Typography
                            variant="body2"
                            style={{ color: '#10b981' }}
                        >
                            File uploaded successfully
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            gutterBottom
                            align="center"
                            style={{ marginBottom: 16 }}
                        >
                            Upload a ZIP file containing lease PDFs to begin
                            processing
                        </Typography>
                        {showAdditiveUploadNote && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                align="center"
                                style={{ marginBottom: 16 }}
                            >
                                New files will be parsed automatically. Document
                                selection will rerun for all resident terms, and
                                previously selected documents may change.
                                Attribute extraction will run automatically for
                                any newly selected documents.
                            </Typography>
                        )}
                    </>
                )}
                {/* Show upload history */}
                {!isLoadingUploadHistory &&
                    uploadHistory &&
                    uploadHistory.length > 0 && (
                        <UploadHistorySection records={uploadHistory} />
                    )}
                {/* Only show dropzone if not currently processing extraction and we don't already have a file in UPloaded status */}
                {/* The backend is expecting to only parse one zip at a time */}
                {!isProcessingExtraction && !hasUploadedFile && (
                    <>
                        <Dropzone
                            onDrop={handleFileDrop}
                            accept={{ 'application/zip': ['.zip'] }}
                            multiple={false}
                            disabled={isUploading}
                        >
                            {({
                                getRootProps,
                                getInputProps,
                                isDragActive,
                            }) => (
                                <div
                                    {...getRootProps()}
                                    className={`${classes.dropzone} ${
                                        isDragActive
                                            ? classes.dropzoneActive
                                            : ''
                                    }`}
                                >
                                    <input {...getInputProps()} />
                                    <UploadIcon
                                        style={{
                                            fontSize: 40,
                                            marginBottom: 12,
                                        }}
                                        color="action"
                                    />
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        gutterBottom
                                    >
                                        {isUploading
                                            ? 'Uploading...'
                                            : 'Drop a ZIP file here, or click to select'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        Accepted format: .zip (containing PDF
                                        files)
                                    </Typography>
                                </div>
                            )}
                        </Dropzone>
                        {uploadProgress && (
                            <UploadProgressDisplay progress={uploadProgress} />
                        )}
                    </>
                )}
                {uploadError && (
                    <Box style={{ marginTop: 16 }}>
                        <Typography variant="body2" color="error">
                            Error: {uploadError}
                        </Typography>
                        {lastFailedFile && (
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={handleRetryUpload}
                                disabled={isUploading}
                                style={{ marginTop: 8 }}
                            >
                                Retry Upload
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
